/*
 * Ecranul 2b: fisa unui serviciu.
 *
 * Pasii se arata inainte de comanda, cu dependentele lor. Contractul o cere
 * explicit - "un serviciu cerut fara sa se stie din ce e compus e o comanda
 * semnata in alb" - si e acelasi lucru pe care omul il vede mai tarziu ca
 * taskuri, doar ca inainte de a apasa. Asa intelege de ce unele lucruri incep
 * imediat si altele asteapta.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as shop from '../api/shop';
import type { CatalogItemDetail, Pricing, ServiceWorkflow } from '../api/types';
import { useCounts } from '../components/CountsContext';
import { useToasts } from '../components/ToastContext';
import { ErrorState, InlineAlert, Loading } from '../components/states';
import { componentLine, pricingHeadline } from '../lib/format';
import { useAsync } from '../lib/useAsync';
import { t } from '../lib/i18n';

interface Bundle {
  item: CatalogItemDetail;
}

export function CatalogItemScreen() {
  const { id = '' } = useParams();

  const bundle = useAsync<Bundle>(
    async (signal) => {
      /* Raspunsul vine in plic - {"service": {...}} - ca toate celelalte din
         contract. Fara desfacerea asta, `item` era chiar plicul, si fiecare
         citire din el - nume, pret, categorie - iesea `undefined`. */
      const { service: item } = await shop.catalogItem(id, { signal });

      /* Nu se mai cere si ruta de pasi. Pasii au fost inlocuiti de stadiile
         fluxului, care vin chiar in fisa; un al doilea dus-intors catre o ruta
         a carei tabela nici nu exista intarzia ecranul degeaba. */
      return { item };
    },
    [id],
  );

  if (bundle.status === 'loading') return <Loading label={t('Loading service…')} />;
  if (bundle.status === 'error' && bundle.error) {
    return <ErrorState error={bundle.error} onRetry={bundle.reload} />;
  }
  if (!bundle.data) return null;

  const { item } = bundle.data;
  const workflow = item.workflow ?? null;

  return (
    <>
      <p className="backlink">
        <Link to="/catalog">{t('Back to catalog')}</Link>
      </p>

      <header className="page-head">
        <div>
          <p className="micro-label">{item.category?.name ?? t('Uncategorised')}</p>
          <h1 className="page-title">{item.name}</h1>
          <p className="page-sub">{item.short_description}</p>
        </div>
      </header>

      <div className="split">
        <div className="split__main">
          <section className="panel">
            <h2 className="panel__title">{t('About this service')}</h2>
            <p className="prose">
              {item.description ?? item.long_description ?? item.short_description ?? t('No description provided.')}
            </p>
          </section>

          {/*
              Drumul tichetului, nu o lista de pasi scrisa pe serviciu.
              Titlul nu mai spune "dupa aprobare": aprobarea e ea insasi un
              stadiu din flux, si sunt servicii care nu trec deloc prin ea.
          */}
          <section className="panel">
            <div className="panel__head">
              <h2 className="panel__title">{t('What happens next')}</h2>
              {workflow ? (
                <span className="panel__note">
                  {workflow.stages.length}{' '}
                  {workflow.stages.length === 1 ? 'stage' : 'stages'}
                  {workflow.name ? ` · ${workflow.name}` : ''}
                </span>
              ) : null}
            </div>

            <WorkflowPreview workflow={workflow} />
          </section>
        </div>

        <aside className="split__side">
          <div className="panel">
            <dl className="facts">
              {/* Aceleasi doua nume ca pe card - fisa si cardul arata aceleasi
                  doua cifre, si ar fi fost ciudat sa se cheme altfel aici.
                  Si aceeasi regula: ce n-a venit nu se deseneaza. */}
              {item.pricing ? (
                <>
                  <dt className="micro-label">{t('Service cost')}</dt>
                  <dd className="facts__strong num">{pricingHeadline(item.pricing)}</dd>
                </>
              ) : null}
              {item.sla !== undefined ? (
                <>
                  <dt className="micro-label">{t('Response SLA')}</dt>
                  <dd>{item.sla ?? '—'}</dd>
                </>
              ) : null}
              <dt className="micro-label">{t('Configuration item')}</dt>
              <dd className="num">CI-{item.ci_id}</dd>
            </dl>

            {/* Fisa arata desfasurarea: fiecare componenta cu tipul si perioada
                ei. Un serviciu cu 18000 fix + 450 pe element trebuie sa arate
                amandoua, nu doar cifra din card. */}
            {item.pricing ? <PricingBreakdown pricing={item.pricing} /> : null}

            <AddToCartForm item={item} />
          </div>
        </aside>
      </div>
    </>
  );
}

/**
 * Desfasurarea pretului pe fisa: fiecare componenta pe linia ei.
 *
 * `free` nu deseneaza nimic - linia "No charge" de sus e de ajuns, iar o lista
 * goala sub ea ar fi zgomot. Cand exista componente, se arata toate: asa un
 * serviciu variabil sau mixt spune din ce se compune pretul, nu doar cifra
 * scurta a cardului.
 */
function PricingBreakdown({ pricing }: { pricing?: Pricing }) {
  /*
   * Plasa, nu decor.
   *
   * Un serviciu fara pret configurat, sau un raspuns caruia ii lipseste campul,
   * nu are voie sa lase pagina alba - si exact asta se intampla pana acum: fisa
   * cadea aici, la prima citire din `pricing`, si nu se mai desena nici numele
   * serviciului. Lipsa costului e o sectiune care nu se arata, nu un ecran gol.
   */
  if (!pricing || !Array.isArray(pricing.components)) {
    return null;
  }

  if (pricing.kind === 'free' || pricing.components.length === 0) {
    return null;
  }

  return (
    <div className="pricing-breakdown">
      <p className="micro-label">{t('What makes up the cost')}</p>
      <ul className="pricing-breakdown__list">
        {pricing.components.map((component, index) => (
          <li className="pricing-breakdown__item num" key={`${component.type}-${index}`}>
            {componentLine(component)}
          </li>
        ))}
      </ul>
      {pricing.kind === 'variable' || pricing.kind === 'mixed' ? (
        <p className="pricing-breakdown__note muted">
          {/* Variabilul depinde de cate elemente, utilizatori sau tichete sunt,
              deci nu exista o singura cifra dinainte. */}
          Usage-based parts are billed on the actual monthly counts.
        </p>
      ) : null}
    </div>
  );
}

function WorkflowPreview({ workflow }: { workflow: ServiceWorkflow | null }) {
  /*
   * Trei raspunsuri diferite, nu unul singur.
   *
   * "N-are flux legat" e o configurare care lipseste si se poate repara - se
   * spune unde. "Are flux fara stadii" e altceva, si tot o lipsa. Ecranul de
   * dinainte le spunea pe amandoua la fel, si mai spunea asta pentru orice
   * serviciu, fiindca se uita la o tabela care nu exista.
   */
  if (!workflow) {
    return (
      <p className="muted">
            {t('The ticket type of this service has no workflow yet, so the stages cannot be shown. The service can still be requested.')}
          </p>
    );
  }

  if (workflow.stages.length === 0) {
    return <p className="muted">{t('This workflow has no stages configured yet.')}</p>;
  }

  return (
    <ol className="stages">
      {workflow.stages.map((stage) => (
        <li className="stages__item" key={stage.status_id}>
          <div className="stages__top">
            <h3 className="stages__title">
              {/* Culoarea statusului e cea din portal, ca stadiul sa arate la
                  fel aici si pe tichet. Fara ea ramane doar numele. */}
              {stage.colour ? (
                <span
                  className="stages__dot"
                  style={{ background: stage.colour }}
                  aria-hidden="true"
                />
              ) : null}
              {stage.name}
            </h3>
          </div>
          {/* Cine preia nu se arata aici.
              Ecranul e al celui care cere, si el vrea sa stie prin ce trece
              cererea, nu care echipa interna o ia la fiecare pas. Numele
              grupurilor sunt oricum interne - "Nivel 2 Retea" nu inseamna nimic
              pentru cine cere un ATM - si l-ar pune sa se intrebe daca trebuie
              sa faca ceva cu ele.
              Campul ramane in api si in tipuri: acolo e corect, si se vede in
              portal, unde chiar conteaza cine preia. */}
        </li>
      ))}
    </ol>
  );
}

function AddToCartForm({ item }: { item: CatalogItemDetail }) {
  const { push } = useToasts();
  const { refreshCart } = useCounts();
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const parsed = Number.parseInt(quantity, 10);
    const amount = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;

    setBusy(true);
    try {
      await shop.addToCart(item.id, amount, note.trim());
      push(`${item.name} added to cart.`);
      refreshCart();
      setNote('');
      setQuantity('1');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('Could not add to cart.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="side-form" onSubmit={submit}>
      {error ? <InlineAlert>{error}</InlineAlert> : null}

      <div className="field">
        <label className="field__label" htmlFor="add-qty">{t('Quantity')}</label>
        <input
          id="add-qty"
          className="field__control"
          type="number"
          min={1}
          step={1}
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
        />
      </div>

      {/*
          Optionala aici, spre deosebire de justificarea de la trimitere.
          Aia e "de ce am nevoie" si o cere cel care decide; asta e "ce mai
          trebuie stiut despre bucata asta" si se scrie doar cand chiar e ceva
          de spus. Doua campuri obligatorii, unul dupa altul, ar face din al
          doilea o formalitate completata cu o liniuta.
      */}
      <div className="field">
        <label className="field__label" htmlFor="add-note">{t('Additional note')}<span className="field__optional muted">(optional)</span>
        </label>
        <textarea
          id="add-note"
          className="field__control"
          rows={3}
          placeholder={t('Anything the team should know about this one.')}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>

      <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
        {busy ? t('Adding…') : t('Add to cart')}
      </button>
    </form>
  );
}
